import { Component, computed, DestroyRef, effect, inject, input, output, signal } from '@angular/core';
import { Country, QuotationForm, QuotationFormGroup, Retailer } from '@root/app.interfaces';
import { FormBuilder, Validators } from '@angular/forms';
import { VerificationTimerService } from '@core/services/verification-timer/verification-timer.service';
import { Countries, STORAGE_NAMES } from '@root/app.config';
import { SessionStorageService } from '@core/services/session-storage/session-storage.service';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { ShareService } from '@core/services/share-data/share-data.service';
import { createPendingSubmitWaiter, hasEmptyRequiredField } from '@features/contact-form/form.helpers';
import { ScrollService } from '@core/services/scroll/scroll.service';
import { formatAddressSuggestionText } from '@shared/form-fields/places-autocomplete/helpers/address-suggestion.helper';
import { formatPostcodeSuggestionNumber } from '@shared/form-fields/places-autocomplete/helpers/postcode-suggestion.helper';
import { AutocompleteSuggestion } from '@shared/form-fields/places-autocomplete/places-autocomplete.component';
import { getAdministrativeAreas } from '@core/administrative-areas';
import { postcodeStateMatchValidator } from './quotation-form.validators';
import { filter, take } from 'rxjs';

@Component({
	selector: 'app-quotation-form',
	templateUrl: './quotation-form.component.html',
	styleUrls: [
		'../contact-form/contact-form.component.scss',
		'../contact-form/contact-from.responsive.scss',
		'./quotation-form.component.scss',
		'./quotation-form.responsive.scss',
	],
})
export class QuotationFormComponent {
	protected get f() {
		return this.form.controls;
	}

	protected get someRequiredFieldsIsEmpty() {
		return hasEmptyRequiredField(this.f);
	}
	protected readonly formatInstallationAddressFn = formatAddressSuggestionText;
	protected readonly Validators = Validators;

	private readonly fb = inject(FormBuilder);
	private readonly destroyRef = inject(DestroyRef);
	private readonly verificationTimerService = inject(VerificationTimerService);
	private readonly sessionStorageService = inject(SessionStorageService);
	private readonly shareService = inject(ShareService);
	private readonly scrollService = inject(ScrollService);

	loading = input<boolean>(false);
	emailVerified = input<boolean>(false);
	priceDetails = input<{ total: number; deposit: number }>();
	savedForm = input<QuotationForm>();

	protected showInstallationAddressBlock = computed(() => this.emailVerified());
	protected countryCode = toSignal<string | null>(this.shareService.getCountry, {
		initialValue: this.sessionStorageService.getSession(STORAGE_NAMES.zip_country_code),
	});

	protected country = computed(() => {
		const countryCode = this.countryCode() ?? 'au';
		const country = Countries.find((country) => country.code === countryCode);
		return {
			...country,
			id: country.code,
			title: country.name,
		};
	});

	protected administrativeAreas = computed(() => getAdministrativeAreas(this.countryCode()));

	protected submitEvent = output<QuotationForm>();
	protected submitted = signal(false);

	protected embeddedRetailer = signal<Retailer | null>(this.sessionStorageService.getBlindData(STORAGE_NAMES.zip_embedded_retailer));

	protected readonly form: QuotationFormGroup = this.fb.group(
		{
			first_name: ['', [Validators.required, Validators.minLength(2)]],
			last_name: ['', [Validators.required, Validators.minLength(2)]],
			phone: ['', [Validators.required, Validators.minLength(3)]],
			post_code: [''],
			email: ['', [Validators.required]],
			comments: [null as string | null, [Validators.minLength(10)]],
			contact: [''],
			installation_address: [''],
			country: [{ value: (this.country() as Country) ?? null, disabled: true }],
			suburb: [''],
			state: [null],
		},
		{ validators: postcodeStateMatchValidator },
	);

	private readonly waitForPendingAndSubmit = createPendingSubmitWaiter(this.form, () => this.onSubmit());
	private submittedEmail = '';

	protected readonly formatPostcodeFn = formatPostcodeSuggestionNumber;

	private readonly installationAddressControls = ['installation_address', 'country', 'suburb', 'state'] as const;

	constructor() {
		toObservable(this.savedForm)
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe((savedForm) => this.syncSavedForm(savedForm));

		effect(() => {
			this.syncInstallationAddressValidators(this.emailVerified());
		});

		toObservable(this.emailVerified)
			.pipe(filter(Boolean), take(1), takeUntilDestroyed(this.destroyRef))
			.subscribe(() => this.submitted.set(false));
	}

	getFormValues(): QuotationForm {
		return this.form.getRawValue();
	}

	private syncInstallationAddressValidators(emailVerified: boolean): void {
		for (const name of this.installationAddressControls) {
			const control = this.form.controls[name];
			emailVerified ? control.addValidators(Validators.required) : control.removeValidators(Validators.required);
			control.updateValueAndValidity({ emitEvent: false });
		}

		const postCode = this.form.controls.post_code;
		const postCodeRequired = emailVerified && !this.embeddedRetailer();
		postCodeRequired ? postCode.addValidators(Validators.required) : postCode.removeValidators(Validators.required);
		postCode.updateValueAndValidity({ emitEvent: false });
	}

	private syncSavedForm(savedForm: QuotationForm) {
		if (!savedForm) {
			return;
		}

		this.form.patchValue({
			first_name: savedForm.first_name ?? '',
			last_name: savedForm.last_name ?? '',
			phone: savedForm.phone ?? '',
			post_code: savedForm.post_code ?? '',
			email: savedForm.email ?? '',
			comments: savedForm.comments ?? null,
			contact: savedForm.contact ?? '',
			installation_address: savedForm.installation_address ?? '',
			country: savedForm.country ?? this.country(),
			suburb: savedForm.suburb ?? '',
			state: savedForm.state ?? null,
		});
	}

	protected onSubmit(): void {
		this.submitted.set(true);

		if (this.form.invalid) {
			return;
		}

		if (this.form.pending) {
			this.waitForPendingAndSubmit();
			return;
		}

		const fromRawValue = this.form.getRawValue();
		this.checkEmailInputHandler(fromRawValue.email);
		this.submitEvent.emit(this.form.getRawValue());
	}

	private checkEmailInputHandler(email: string): void {
		if (this.submittedEmail !== email && this.verificationTimerService.isTimerActive) {
			this.verificationTimerService.stopTimer();
		}

		this.submittedEmail = email;
	}

	protected scrollToError(event: Event): void {
		if (this.form.invalid) {
			this.scrollService.scrollToFormError(event);
		}
	}

	protected onSelectInstallationAddress(suggestion: AutocompleteSuggestion | null): void {
		const details = suggestion?.addressDetails;
		const state = details?.state ? this.administrativeAreas().find((area) => area.id === details.state.toLowerCase()) : null;

		if (!state) {
			return;
		}

		this.form.patchValue({
			suburb: details?.suburb ?? null,
			post_code: details?.postcode ?? null,
			state,
		});
	}
}
